import { Form, Input, Select, TreeSelect } from "antd";
import { LocationSearchNotFoundMessages } from "components/widgets/location-search-not-found-messages";
import { ProjectContext } from "context/ProjectProvider";
import { TFetureDetailUpdateAction } from "entity-app/models";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useShippingLocation } from "hooks/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { updatePropertyAtPath } from "utils/utils";
import { QUERY_MASTER_UNITS } from "services/graphQL/queries";
import {
  getTreeFromMeasuremntList,
  UnitType
} from "../material-details-tab/units-tree";

export default function MaterialCharacteristicsCard(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  disabled: boolean;
  materialHeaderMap: any;
  material: any;
}) {
  const {
    featureInstanceData,
    actions,
    disabled,
    materialHeaderMap,
    material
  } = props;
  const [form] = Form.useForm();
  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);
  const [localFeatureInstanceData, setLocalFeatureInstanceData] =
    useState<any>();

  useEffect(() => {
    if (!featureInstanceData) return;
    setLocalFeatureInstanceData(structuredClone(featureInstanceData));
  }, [featureInstanceData]);

  const {
    searchLocation,
    fetching,
    selectOptions: shippingLocationOptionResults,
    searchedString
  } = useShippingLocation({
    gqlClientForProject,
    projectToken: tokenRetrievalState?.token
  });

  const { data: allUnits } = useCIQQuery<{
    material_quantity_unit: Array<UnitType>;
    material_size_unit: Array<UnitType>;
  }>(QUERY_MASTER_UNITS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const featureMaterial = useMemo(() => {
    if (!localFeatureInstanceData?.feature_materials?.length) return null;
    return localFeatureInstanceData?.feature_materials[0];
  }, [localFeatureInstanceData?.feature_materials]);

  const updateFeatureMaterial = (updates: Record<string, any>) => {
    setLocalFeatureInstanceData(
      updatePropertyAtPath(localFeatureInstanceData, "feature_materials", [
        {
          ...featureMaterial,
          ...updates
        }
      ])
    );
  };

  const shippingLocationOptions = useMemo(() => {
    if (!searchedString && material?.place_id) {
      return [
        {
          label: material.project_location?.location_service?.label,
          value: material?.place_id
        }
      ];
    }
    return shippingLocationOptionResults;
  }, [material, shippingLocationOptionResults, searchedString]);

  const onShippingLocationChange = (selection: any) => {
    if (selection) {
      updateFeatureMaterial({ place_id: selection ? selection.value : null });
      actions.updateFeatureShippingLocation({
        placeId: selection ? selection.value : null,
        featureId: featureMaterial?.id
      });
    }
  };

  const warrantList = useMemo(
    () =>
      [...Array(21).keys()].map((v: number) => {
        if (v === 20) return { label: "Lifetime warranty", value: 100 };
        return {
          label: `${v + 1} years`,
          value: v + 1
        };
      }),
    []
  );

  useEffect(() => {
    if (!featureMaterial) return;
    form.setFieldsValue({
      material_size: featureMaterial?.material_size,
      material_size_unit_id: featureMaterial?.material_size_unit
        ? `${featureMaterial?.material_size_unit.measurement} ${featureMaterial?.material_size_unit.unit}`
        : undefined,
      quantity: featureMaterial?.quantity,
      material_quantity_unit_id: featureMaterial?.material_quantity_unit
        ? `${featureMaterial?.material_quantity_unit.measurement} ${featureMaterial?.material_quantity_unit.unit}`
        : undefined,
      warranty: featureMaterial?.warranty,
      manufacturer: featureMaterial?.manufacturer,
      place_id: featureMaterial?.place_id,
      part_number: featureMaterial?.part_number,
      serial_number: featureMaterial?.serial_number
    });
  }, [featureMaterial, form]);

  return (
    <div
      className="grow h-[380px] overflow-auto"
      data-testid="material-characteristics-root"
    >
      <Form
        form={form}
        layout="vertical"
        data-testid="material-characteristics-form"
      >
        <Form.Item
          label={materialHeaderMap?.shipping_location}
          name="place_id"
          data-testid="material-characteristics-shipping-location-item"
        >
          <Select
            placeholder="Search by Address, City or State"
            allowClear
            showSearch
            filterOption={false}
            labelInValue
            disabled={disabled}
            loading={fetching}
            options={shippingLocationOptions}
            onSearch={searchLocation}
            onChange={onShippingLocationChange}
            onClear={() => {
              updateFeatureMaterial({ place_id: null });
              actions.updateFeatureShippingLocation({
                placeId: null,
                featureId: featureMaterial?.id
              });
            }}
            notFoundContent={
              <LocationSearchNotFoundMessages
                searchedString={searchedString}
                fetching={fetching}
                data-testid="material-characteristics-shipping-location-not-found"
              />
            }
            data-testid="material-characteristics-shipping-location-select"
            // allowClear enable this if asked
          />
        </Form.Item>
        <Form.Item
          label={materialHeaderMap?.manufacturer}
          name="manufacturer"
          data-testid="material-characteristics-manufacturer-item"
        >
          <Input
            placeholder="Enter Manufacturer"
            disabled={disabled}
            onBlur={async (event: any) => {
              if (
                (featureMaterial?.manufacturer ?? "") ===
                event?.target?.value?.trim()
              )
                return;
              actions.updateMaterialFields({
                manufacturer: event.target.value?.trim()
              });
              updateFeatureMaterial({
                manufacturer: event.target.value?.trim()
              });
            }}
            data-testid="material-characteristics-manufacturer-input"
          />
        </Form.Item>
        <Form.Item
          label={materialHeaderMap?.part_number}
          name="part_number"
          data-testid="material-characteristics-part-number-item"
        >
          <Input
            placeholder="Enter part number"
            disabled={disabled}
            onBlur={async (event: any) => {
              if (
                (featureMaterial?.part_number ?? "") ===
                event?.target?.value?.trim()
              )
                return;
              actions.updateMaterialFields({
                part_number: event.target.value?.trim()
              });
              updateFeatureMaterial({
                part_number: event.target.value?.trim()
              });
            }}
            data-testid="material-characteristics-part-number-input"
          />
        </Form.Item>
        <Form.Item
          label={materialHeaderMap?.serial_number}
          name="serial_number"
          data-testid="material-characteristics-serial-number-item"
        >
          <Input
            placeholder="Enter serial number"
            disabled={disabled}
            onBlur={async (event: any) => {
              if (
                (featureMaterial?.serial_number ?? "") ===
                event?.target?.value?.trim()
              )
                return;
              actions.updateMaterialFields({
                serial_number: event.target.value?.trim()
              });
              updateFeatureMaterial({
                serial_number: event.target.value?.trim()
              });
            }}
            data-testid="material-characteristics-serial-number-input"
          />
        </Form.Item>
        <Form.Item
          label={materialHeaderMap?.warranty}
          name="warranty"
          data-testid="material-characteristics-warranty-item"
        >
          <Select
            placeholder="Select warranty"
            disabled={disabled}
            options={warrantList}
            onChange={(value: any) => {
              if (featureMaterial?.warranty === value) return;
              updateFeatureMaterial({ warranty: value ?? null });
              actions.updateMaterialFields({
                warranty: value ?? null
              });
            }}
            allowClear
            data-testid="material-characteristics-warranty-select"
          />
        </Form.Item>
        <div
          className="grid grid-cols-2 gap-x-2"
          data-testid="material-characteristics-quantity-grid"
        >
          <Form.Item
            label={materialHeaderMap?.quantity}
            name="quantity"
            data-testid="material-characteristics-quantity-item"
          >
            <Input
              type="number"
              placeholder="Enter Quantity"
              className="w-full"
              disabled={disabled}
              min={0}
              onKeyDown={(evt) => evt.key === "e" && evt.preventDefault()}
              onBlur={(event: any) => {
                if (featureMaterial?.quantity === Number(event.target.value))
                  return;
                const value = Number(event.target.value.replace(/\D/g, ""));
                const oldValue = featureMaterial?.quantity || 0;
                if (oldValue !== value)
                  actions.updateMaterialFields({
                    quantity: value
                  });
                updateFeatureMaterial({ quantity: value });
              }}
              data-testid="material-characteristics-quantity-input"
            />
          </Form.Item>
          <Form.Item
            label={materialHeaderMap?.quantity_unit_id}
            name="material_quantity_unit_id"
            data-testid="material-characteristics-quantity-unit-item"
          >
            <TreeSelect
              placeholder="Enter Quantity Unit"
              disabled={disabled}
              treeData={
                allUnits
                  ? getTreeFromMeasuremntList(allUnits.material_quantity_unit)
                  : []
              }
              onChange={(value: any) => {
                const data = allUnits?.material_quantity_unit?.find(
                  (x) => x.id === value
                );

                updateFeatureMaterial({
                  material_quantity_unit: value
                    ? {
                        id: data?.id,
                        unit: data?.unit,
                        measurement: data?.measurement
                      }
                    : null
                });
                if (featureMaterial?.material_quantity_unit?.id === value)
                  return;
                actions.updateMaterialFields({
                  quantity_unit_id: value ?? null
                });
              }}
              treeDefaultExpandAll
              allowClear
              data-testid="material-characteristics-quantity-unit-treeselect"
            />
          </Form.Item>
        </div>
        <div
          className="grid grid-cols-2 gap-x-2"
          data-testid="material-characteristics-size-grid"
        >
          <Form.Item
            label={materialHeaderMap?.material_size}
            name="material_size"
            data-testid="material-characteristics-material-size-item"
          >
            <Input
              placeholder="Enter Material Size"
              className="w-full"
              disabled={disabled}
              onBlur={(event: any) => {
                const value = event.target.value?.trim();
                const oldValue = featureMaterial?.material_size || "";
                if (oldValue !== value)
                  actions.updateMaterialFields({
                    material_size: value
                  });
                updateFeatureMaterial({ material_size: value });
              }}
              data-testid="material-characteristics-material-size-input"
            />
          </Form.Item>
          <Form.Item
            label={materialHeaderMap?.size_unit_id}
            name="material_size_unit_id"
            data-testid="material-characteristics-size-unit-item"
          >
            <TreeSelect
              placeholder="Enter Material Size Unit"
              disabled={disabled}
              treeData={
                allUnits
                  ? getTreeFromMeasuremntList(allUnits.material_size_unit)
                  : []
              }
              onChange={(value: any) => {
                const data = allUnits?.material_size_unit.find(
                  (x) => x.id === value
                );

                updateFeatureMaterial({
                  material_size_unit: value
                    ? {
                        id: data?.id,
                        unit: data?.unit,
                        measurement: data?.measurement
                      }
                    : null
                });
                if (featureMaterial?.material_size_unit?.id === value) return;
                actions.updateMaterialFields({
                  size_unit_id: value ?? null
                });
              }}
              treeDefaultExpandAll
              allowClear
              data-testid="material-characteristics-size-unit-treeselect"
            />
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}
